import React from "react";
import { Box, Card, CardContent, Typography, Button } from "@mui/material";
import TickIcon from "../../../assets/icons/tick-icon.svg";
import "./subscription-card.scss";
import {
  capitalizeFirstLetter,
  formatTitleCase,
  formatWithDollar,
} from "../../../utils/utils";
import { bgColors } from "../../../features/tenant/add-tenant/subscriptions-plans/subscription-plans";
import icons from "../../../assets/icons/icons";

interface CardData {
  cardBgColor: string;
  cardTitle: string;
  secondaryTxtColor: string;
  secondaryTxt: string;
  price: string;
  suggestionTxt: string;
  features: string[];
  isPopular?: boolean;
}

interface SubscriptionCardProps {
  item: any;
  onClick: (item: CardData) => void;
  selectedItem: any;
  bgColor?: string;
  isShowActiveBadge?: boolean;
  buttonText?: string;
  isPending?: boolean;
  activePlan?: any;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  item,
  onClick,
  selectedItem,
  bgColor,
  isShowActiveBadge = false,
  buttonText = "Choose Plan",
  isPending = false,
  activePlan,
}) => {
  const color: string = item?.theme_color || bgColor || bgColors[0];
  return (
    <div>
      <Box
        sx={{
          position: "relative",
          // width: "264.57px",
          width: "275.57px",
          height: "603px",
          padding: "22px",
          boxSizing: "border-box",
          background: "#ffffff",
          borderRadius: "16px",
          // border: `1.5px solid ${item?.plan_name?.toUpperCase() === "STARTER" && color}`,
          boxShadow:
            (selectedItem?.id === item?.id &&
              selectedItem?.plan_type === item?.plan_type) ||
            isShowActiveBadge
              ? `0px 2px 8px ${color}`
              : 0,
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Card
            sx={{
              borderRadius: "20px",
              textAlign: "center",
              background: color || "#4B3B31",
            }}
          >
            <CardContent>
              <Typography
                variant="h5"
                component="div"
                sx={{
                  m: 0,
                  fontWeight: 700,
                  fontFamily: "Roboto",
                  color: "#ffffff",
                  textTransform: "uppercase",
                }}
              >
                {item?.plan_name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  //   marginTop: "5px",
                  mb: 3,
                  color: item?.secondaryTxtColor || "#E4E4E4",
                  fontWeight: 400,
                  fontSize: "13px",
                  lineHeight: "30.46px",
                }}
              >
                {capitalizeFirstLetter(item?.plan_type)} Plan
              </Typography>
            </CardContent>
          </Card>
          <Box
            sx={{
              position: "absolute",
              bottom: -30,
              left: "50%",
              transform: "translateX(-50%)",
              width: "65.45px",
              height: "65.45px",
              borderRadius: "50%",
              backgroundColor: "#ffffff",
              color: color || "#4B3B31",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              fontWeight: 700,
              fontSize: item?.price?.includes("$") ? "18.7px" : "12px",
              fontFamily: "Roboto",
            }}
          >
            {formatWithDollar(item?.price)}
          </Box>
          <Box
            sx={{
              position: "absolute",
              bottom: "-7%",
              left: "50%",
              transform: "translateX(-50%)",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0px 10px",
            }}
          >
            <Box
              className="line left"
              sx={{
                backgroundColor: color || "#4B3B31",
                "&::before": {
                  backgroundColor: color || "#4B3B31",
                },
              }}
            ></Box>
            <Box
              className="line right"
              sx={{
                backgroundColor: color || "#4B3B31",
                "&::before": {
                  backgroundColor: color || "#4B3B31",
                },
              }}
            ></Box>
          </Box>
        </Box>
        <Typography
          variant="body1"
          component="div"
          align="center"
          color="#929292"
          sx={{
            fontWeight: 400,
            fontFamily: "Roboto",
            fontSize: "14px",
            marginTop: "35px",
            letterSpacing: 0,
          }}
        >
          {item?.suited_for}
        </Typography>
        {isShowActiveBadge && (
          <Box className="active-plan-wrapper">
            <Box className="active-plan-container">
              <Box className="active-plan-wrapper-dot"></Box>Active Plan
            </Box>
          </Box>
        )}
        {isPending && (
          <Box className="active-plan-wrapper">
            <Box className="active-plan-container pending-plan">
              <Box className="active-plan-wrapper-dot"></Box>Pending Plan
            </Box>
          </Box>
        )}
        <Box>
          <Box
            sx={{
              fontWeight: 500,
              fontFamily: "Roboto",
              fontSize: "14px",
              marginTop: isShowActiveBadge ? "15px" : "30px",
              textAlign: "center",
            }}
          >
            Plan Inclusions
          </Box>
          <ul className="custom-list">
            {item?.no_of_users ? (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                {item?.no_of_users > 0 ? item?.no_of_users : "Unlimited"} Users
              </li>
            ) : (
              <></>
            )}
            {item?.no_of_leads || item?.no_of_contacts ? (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                {item?.no_of_leads
                  ? `${
                      item?.no_of_leads > 0 ? item?.no_of_leads : "Unlimited"
                    } Leads`
                  : ""}{" "}
                {item?.no_of_contacts
                  ? ` and ${item?.no_of_contacts > 0 ? item?.no_of_contacts : "Unlimited"}  Contacts`
                  : ""}
              </li>
            ) : (
              <></>
            )}
            {item?.storage_limits ? (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                {item?.storage_limits} data storage limit
              </li>
            ) : (
              <></>
            )}
            {item?.api_calls_per_month ? (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                {item?.api_calls_per_month > 0
                  ? item?.api_calls_per_month
                  : "Unlimited"}{" "}
                API calls per month
              </li>
            ) : (
              <></>
            )}
            {item?.automation_rules_limit ? (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                {item?.automation_rules_limit > 0
                  ? item?.automation_rules_limit
                  : "Unlimited"}{" "}
                Custom Automation Rules
              </li>
            ) : (
              <></>
            )}
            {item?.support_level && (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                {formatTitleCase(item?.support_level)}
              </li>
            )}
            {item?.reporting_level && (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                Reports
              </li>
            )}
            {item?.ai_chatbot_availability && (
              <li>
                <img src={TickIcon} alt="tick-icon" />
                AI Chatbot
              </li>
            )}
          </ul>
        </Box>
        {/* {item?.plan_name?.toUpperCase() === "STARTER" && (
          <Box className="popular-plan" sx={{ background: color || "#4B3B31" }}>
            <span>Most Popular</span>
          </Box>
        )} */}

        {isShowActiveBadge &&
          Number(activePlan?.price) !== Number(item?.price) && (
            <Box className="plan-price-update">
              <img src={icons.TermOfServicesInfo} alt="img" />
              <span>
                Your plan price updated from ${activePlan?.price} to $
                {item?.price}, Updated price will be applicable from your next
                billing cycle.
              </span>
            </Box>
          )}
        {!isShowActiveBadge && !isPending && (
          <Box className="plan-button-wrapper">
            <button
              className={`plan-button ${
                selectedItem?.id === item?.id &&
                selectedItem?.plan_type === item?.plan_type
                  ? "active-button-plan"
                  : ""
              }`}
              onClick={() => {
                onClick(item);
              }}
              disabled={item?.plan_name?.toLowerCase() === "enterprise"}
              type="button"
            >
              {item?.plan_name?.toLowerCase() === "enterprise"
                ? "Coming Soon"
                : selectedItem?.id === item?.id &&
                    selectedItem?.plan_type === item?.plan_type
                  ? "Selected"
                  : buttonText}
            </button>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default SubscriptionCard;
